import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Box, ListItem, ListItemIcon, ListItemText, MenuItem, MenuList, Stack, Theme, Typography } from "@mui/material";

import { signOut } from "aws-amplify/auth";
import { useDispatch, useSelector } from "react-redux";
import { ReactRouterAppProvider } from "@toolpad/core/react-router";
import { Navigation, NavigationPageItem } from "@toolpad/core/AppProvider";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import { Loader } from "@components/Loader";
import { RootState, store } from "@store/store";
import { PopupManager } from "@components/PopupManager";
import { DialogManager } from "@components/DialogManager";
import { setExpandStateLayoutSidebar, setPopupConfig } from "@store/slices/componentsSlice";
import {
    DashboardHeaderProps,
    DashboardLayout,
    DashboardSidebarPageItem,
    SidebarFooterProps,
} from "@toolpad/core/DashboardLayout";

import { PyMioTheme } from "@themes/PyMioTheme.ts";
import { FinbraTheme } from "@themes/FinbraTheme.ts";
import { KapitalFlexTheme } from "@themes/KapitalFlexTheme.ts";
import { BackOfficeTheme } from "@themes/BackOfficeTheme.ts";
import { MiCrediMotoTheme } from "@themes/MiCredimotoTheme.ts";

import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { CustomHeader } from "@components/CustomHeader.tsx";
import { useGetPermission } from "@hooks/useGetPermission";
import { useTokenExpirationGuard } from "@hooks/useTokenExpirationGuard";
import { useLazyGetTaxSystemQuery } from "@services/api/catalogs";

export const SolicitudLayout = () => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [themeToLoad, setThemeToLoad] = useState<Theme>(BackOfficeTheme);
    const [bpmLateralOptions, setBpmLateralOptions] = useState<Navigation>([]);

    const permission = useGetPermission("MEEM");

    const sessionInfo = (state = store.getState()) => state.session;
    const expandLayoutSidebarState = (state = store.getState()) => state.components.layout.sidebar;

    const userSession = useSelector((state: RootState) => state.session.user);

    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);
    const lockMenuApplication = useSelector((state: RootState) => state.components.menuApplication.lock);

    const [triggerGetTaxSystemInfo] = useLazyGetTaxSystemQuery();

    useTokenExpirationGuard();

    const disableByPermission = (text: string) => {
        switch (text) {
            case "Info. General":
                return !permission?.children.some((permission) => permission.code === "MIGR");

            case "Perfil Financiero":
                return !permission?.children.some((permission) => permission.code === "MPCR");

            case "Perfil Legal":
                return !permission?.children.some((permission) => permission.code === "MRPPL");

            case "Oferta":
                return !permission?.children.some((permission) => permission.code === "MOFER");

            case "Dictamen Legal":
                return !permission?.children.some((permission) => permission.code === "MDIC");

            case "Expediente":
                return !permission?.children.some((permission) => permission.code === "MEXE");

            // case "Validación Crediticia":
            //     return !permission?.children.some((permission) => permission.code === "MVCR");

            case "Riesgo e Identidad":
                return !permission?.children.some((permission) => permission.code === "MRIID");

            case "PLD Integral":
                return !permission?.children.some((permission) => permission.code === "MPLDI");

            case "Visita Ocular":
                return !permission?.children.some((permission) => permission.code === "MVOC");

            case "Revisión Control":
                return !permission?.children.some((permission) => permission.code === "MCCO");

            case "Contractuales":
                return !permission?.children.some((permission) => permission.code === "MCON");

            case "Cartera y Dispersión":
                return !permission?.children.some((permission) => permission.code === "MDIS");

            case "Resumen Operación":
                return !permission?.children.some((permission) => permission.code === "MRCR");
        }
    };

    const disableByProgress = (text: string) => {
        let step = 0;
        const step_status: Array<Array<string>> = [
            [
                "CSDUS",
                "CSRUS",
                "CSVTE",
                "CSDEM",
                "CSAEM",
                "CSUDC",
                "CSABA",
                "CSIGA",
                "CSPDI",
                "CSDAL",
                "CSCDA",
                "CSDAV",
                "CSCOF",
                "CSBBC",
                "CREVC",
                "CSREV",
                "CRECC",
                "CSREC",
                "CSRER",
                "CSCOM",
                "CSOFE",
                "CSEOF",
                "CRSO",
                "CSRES",
                "COFAC",
                "CSSSA",
                "CSREO",
                "CSLSA",
                "CSORC",
                //MICREDIMOTO
                "CSIP1",
                "CSIP2",
                "CSDPE",
                "CSIBA",
            ],
            ["CSAOL", "CSAAO", "CSOAC", "CSBEX", "CSDDI", "CSDIC", "CSAED", "CSEDC", "CSECI", "CSRDO"],
            ["CSVCC"],
            ["CSVCL", "CSAVC", "CSVDV", "CSVYC"],
            ["CSRIC", "CSRFO"],
            ["CSDDV", "CSEVD", "CSVDE", "CSBAF", "CSFIR", "CSFRL", "CSVDF", "CSDIS", "CSRCO"],
            ["CSFIE", "CSCLI"],
        ];

        const step_status_index = step_status.findIndex(
            (status) => status.some((st) => st === applicationSelected.status_code) === true,
        );

        switch (text) {
            case "Info. General":
            case "Perfil Financiero":
            case "Perfil Legal":
            case "Oferta":
                step = 0;
                break;
            case "Dictamen Legal":
            case "Expediente":
                step = ["CSA01", "CSA02"].includes(applicationSelected.tax_system.code) ? 1000 : 1;
                break;
            case "Validación Crediticia":
            case "Riesgo e Identidad":
            case "PLD Integral":
                step = ["CSA01", "CSA02"].includes(applicationSelected.tax_system.code) ? 1000 : 2;
                break;
            case "Visita Ocular":
                step = 3;
                break;
            case "Revisión Control":
                step = ["CSA01", "CSA02"].includes(applicationSelected.tax_system.code) ? 1000 : 4;
                break;
            case "Contractuales":
            case "Cartera y Dispersión":
                step = ["CSA01", "CSA02"].includes(applicationSelected.tax_system.code) ? 1000 : 5;
                break;
            case "Resumen Operación":
                step = ["CSA01", "CSA02"].includes(applicationSelected.tax_system.code) ? 1000 : 6;
                break;
        }

        // if (["CSA01", "CSA02"].includes(applicationSelected.tax_system.code)) {
        //     return 1;
        // }

        return step > step_status_index;
    };

    const customHeader = (props: DashboardHeaderProps) => {
        return <CustomHeader {...props} />;
    };

    const sidebarFooter = (props: SidebarFooterProps) => {
        return (
            <Stack direction={"column"}>
                <MenuList>
                    <MenuItem
                        onClick={() =>
                            dispatch(
                                setExpandStateLayoutSidebar(!expandLayoutSidebarState(store.getState()).expandState),
                            )
                        }
                    >
                        <ListItemText>{props.mini ? "" : "Ocultar menú"}</ListItemText>
                        <ListItemIcon>{props.mini ? <MenuIcon /> : <MenuOpenIcon />}</ListItemIcon>
                    </MenuItem>
                </MenuList>
            </Stack>
        );
    };

    const handleLogout = async () => {
        dispatch({ type: "CLEAN_STORE" });
        await signOut();
        navigate("/");
    };

    const handleRenderPageItem = useCallback(
        (item: NavigationPageItem, { mini }: { mini: boolean }) => {
            if (item.segment?.startsWith("header")) {
                return (
                    <ListItem sx={{ justifyContent: "center", p: 0 }}>
                        <Typography component={"span"} variant="h5" sx={{
                            color: "main2",
                            fontWeight: 300
                        }}>
                            {mini === false && `ID Solicitud: `}
                            <Typography component={"span"} variant="h5" sx={{
                                color: "main2",
                                fontWeight: 600
                            }}>
                                {applicationSelected.id_application_company?.toString().padStart(5, "0")}
                            </Typography>
                        </Typography>
                    </ListItem>
                );
            }

            return (
                <DashboardSidebarPageItem
                    item={item}
                    disabled={
                        location.pathname.startsWith("/admin")
                            ? false
                            : disableByProgress(item.title ?? "") ||
                              disableByPermission(item.title ?? "") ||
                              lockMenuApplication
                    }
                />
            );
        },
        [applicationSelected.id, location.pathname, lockMenuApplication, applicationSelected.status_code],
    );

    const loadInfo = async () => {
        const taxSystemInfo = await triggerGetTaxSystemInfo(applicationSelected.tax_system.id).unwrap();

        const dynamicPath = `solicitud/${params["companyName"]}/${params["id"]}`;
        const bpm_lateral_options: Navigation = taxSystemInfo.config_extra.bpm_lateral_options.map((option) => {
            if (option.type === "segment") {
                return {
                    type: "segment",
                    title: option.title,
                    segment: option.segment.replace("#", dynamicPath),
                };
            } else {
                return {
                    kind: "divider",
                };
            }
        });

        setBpmLateralOptions(bpm_lateral_options);
    };

    useEffect(() => {
        dispatch(setPopupConfig());
    }, [location]);

    useEffect(() => {
        if (applicationSelected.id == "0-0-0-0-0") return;

        if (sessionInfo(store.getState()).companies.length == 1) {
            if (sessionInfo(store.getState()).companies.at(0)?.name === "PYMIO") {
                setThemeToLoad(PyMioTheme);
            } else if (sessionInfo(store.getState()).companies.at(0)?.name === "FINBRA") {
                setThemeToLoad(FinbraTheme);
            } else if (sessionInfo(store.getState()).companies.at(0)?.name === "KAPITAL_FLEX") {
                setThemeToLoad(KapitalFlexTheme);
            } else if (sessionInfo(store.getState()).companies.at(0)?.name === "MICREDIMOTO") {
                setThemeToLoad(MiCrediMotoTheme);
            } else {
                setThemeToLoad(BackOfficeTheme);
            }
        } else {
            if (applicationSelected.company_source === "FINBRA") {
                setThemeToLoad(FinbraTheme);
            } else if (applicationSelected.company_source === "KAPITAL_FLEX") {
                setThemeToLoad(KapitalFlexTheme);
            } else if (applicationSelected.company_source === "MICREDIMOTO") {
                setThemeToLoad(MiCrediMotoTheme);
            } else {
                setThemeToLoad(BackOfficeTheme);
            }
        }

        loadInfo();
    }, [sessionInfo(store.getState()).companies, applicationSelected.id]);

    return (
        <ReactRouterAppProvider
            theme={themeToLoad}
            navigation={bpmLateralOptions}
            session={{
                user: {
                    name: userSession.name?.concat(
                        " ",
                        userSession.last_name ?? "",
                        " ",
                        userSession.last_name_2 ?? "",
                    ),
                },
            }}
            authentication={{
                signOut: handleLogout,
                signIn: () => {},
            }}
        >
            <DashboardLayout
                renderPageItem={handleRenderPageItem}
                sidebarExpandedWidth={220}
                slots={{
                    sidebarFooter: sidebarFooter,
                    header: customHeader,
                }}
                sx={{ backgroundColor: "#f1f2f7" }}
            >
                <Suspense fallback={<Loader />}>
                    <Box
                        id="content"
                        sx={{
                            py: 3,
                            px: 4,
                            minHeight: "100%"
                        }}>
                        <Outlet />
                    </Box>
                </Suspense>
            </DashboardLayout>
            <DialogManager />
            <PopupManager />
        </ReactRouterAppProvider>
    );
};
